import React, { useEffect, useState, useMemo } from "react";
import { fetchCustomerDetails } from "../../api";
import { toDubai } from "../../utils/date";
import Pagination from "../../components/Pagination/Pagination";
import styles from "./CustomerPurchaseList.module.css";

const CustomerPurchaseList = () => {
  const [purchases, setPurchases] = useState(
    JSON.parse(sessionStorage.getItem("purchases")) || []
  );
  const [freeServicesLeft, setFreeServicesLeft] = useState(0);
  const [serviceClaims, setServiceClaims] = useState([]);
  const [loading, setLoading] = useState(purchases.length === 0);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const fetchPurchases = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication token not found. Please log in.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");

      const response = await fetchCustomerDetails(token);
      console.log("Customer details response:", response.data);

      const newPurchases = response.data.purchases || [];
      const claims = response.data.service_claims || [];
      const freeServices = response.data.free_stringing_services || 0;

      setPurchases(newPurchases);
      setServiceClaims(claims);
      setFreeServicesLeft(freeServices);

      if (newPurchases.length === 0) {
        sessionStorage.removeItem("purchases");
      } else {
        sessionStorage.setItem("purchases", JSON.stringify(newPurchases));
      }
    } catch (err) {
      console.error("Error fetching purchases:", err);
      const errorMsg =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch purchases. Please try again.";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getFreeServicesLeftFor = (rp_id) => {
    const claim = serviceClaims.find((c) => c.rp_id === rp_id);
    if (!claim) return 0;
    return (claim.total_free_services || 0) - (claim.used_services || 0);
  };

  useEffect(() => {
    fetchPurchases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredPurchases = useMemo(
    () => purchases.filter((p) => parseFloat(p.line_price) > 0),
    [purchases]
  );

  const totalPages = Math.ceil(filteredPurchases.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedItems = filteredPurchases.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  return (
    <div className={styles.purchaseListContainer}>
      <h2 className={styles.title}>My Purchases</h2>

      {loading && (
        <div className={styles.spinnerContainer}>
          <div className={styles.spinner}></div>
        </div>
      )}

      {error && <p className={styles.error}>{error}</p>}

      {!loading && !error && (
        <div>
          {(freeServicesLeft ?? 0) > 0 ? (
            <p className={styles.serviceMessage}>
              As part of your purchases, you still have{" "}
              <strong>{freeServicesLeft}</strong> complimentary stringing{" "}
              {freeServicesLeft === 1 ? "service" : "services"} to enjoy.
              <br />
              <small>
                (Please note: the <strong>stringing service</strong> is free,
                but the strings themselves are not included.)
              </small>
            </p>
          ) : (
            <p className={styles.serviceMessage}>
              Dear Customer, you don’t have any complimentary stringing services
              remaining right now. You can still book a{" "}
              <strong>paid stringing service</strong> anytime.
              <br />
            </p>
          )}

          {purchases.length === 0 ? (
            <div className={styles.emptyState}>
              <p className={styles.message}>No purchases found.</p>
              <p className={styles.submessage}>
                Your racket purchases from our store will appear here.
              </p>
            </div>
          ) : filteredPurchases.length > 0 ? (
            <>
              <table className={styles.purchaseTable}>
                <thead>
                  <tr>
                    <th>Purchase Date</th>
                    <th>Racket Name</th>
                    <th>Price</th>
                    <th>Free Services Left</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedItems.map((purchase, idx) => (
                    <tr key={idx}>
                      <td>{toDubai(purchase.purchase_date)}</td>
                      <td>{purchase.product_name}</td>
                      <td>
                        <span className="dirham-symbol">&#xea;</span>{" "}
                        {purchase.line_price}
                      </td>
                      <td data-value={getFreeServicesLeftFor(purchase.rp_id)}>
                        {getFreeServicesLeftFor(purchase.rp_id)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              <p className={styles.message}>
                All purchases have been filtered (must have price &gt; 0).
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerPurchaseList;
